/**
 * Dashboard Cache Service
 * Caches dashboard-related data to avoid repeated API calls
 * 
 * Cache Strategy:
 * - In-memory cache for fast access during session
 * - sessionStorage for persistence across page refreshes
 * - TTL-based expiration for different data types
 */

class DashboardCache {
  constructor() {
    // In-memory caches
    this.allSitesCache = null;
    this.allSitesCacheTimestamp = null;
    this.allUsersCache = null;
    this.allUsersCacheTimestamp = null;
    this.driverStatsCache = new Map(); // Map<`${rideId}_${userId}`, { data, timestamp }>
    this.driverStatusCache = new Map(); // Map<`${rideId}_${userId}`, { data, timestamp }>
    this.liveRidesCache = new Map(); // Map<date, { data, timestamp }>
    
    // Cache configuration
    this.config = {
      // All sites expire after 30 minutes (sites don't change often)
      ALL_SITES_TTL: 30 * 60 * 1000, // 30 minutes
      // All users expire after 10 minutes (users change more frequently)
      ALL_USERS_TTL: 10 * 60 * 1000, // 10 minutes
      // Driver stats expire after 30 seconds (real-time data)
      DRIVER_STATS_TTL: 30 * 1000, // 30 seconds
      // Driver status expires after 30 seconds (real-time data)
      DRIVER_STATUS_TTL: 30 * 1000, // 30 seconds
      // Live rides expire after 2 minutes (could change frequently)
      LIVE_RIDES_TTL: 2 * 60 * 1000, // 2 minutes
      // Storage key for sessionStorage
      STORAGE_KEY: 'dashboardCache',
    };
    
    // Load cache from sessionStorage on initialization
    this.loadFromStorage();
  }

  /**
   * Get cache key for driver stats/status
   */
  getDriverKey(rideId, userId) {
    return `${rideId}_${userId}`;
  }

  /**
   * Get cache key for live rides
   */
  getLiveRidesKey(date) {
    return `liveRides_${date}`;
  }

  /**
   * Load cache from sessionStorage
   */
  loadFromStorage() {
    try {
      const stored = sessionStorage.getItem(this.config.STORAGE_KEY);
      if (stored) {
        const cacheData = JSON.parse(stored);
        const now = Date.now();
        
        // Load all sites cache
        if (cacheData.allSites && cacheData.allSitesTimestamp) {
          if (now - cacheData.allSitesTimestamp < this.config.ALL_SITES_TTL) {
            this.allSitesCache = cacheData.allSites;
            this.allSitesCacheTimestamp = cacheData.allSitesTimestamp;
          }
        }
        
        // Load all users cache
        if (cacheData.allUsers && cacheData.allUsersTimestamp) {
          if (now - cacheData.allUsersTimestamp < this.config.ALL_USERS_TTL) {
            this.allUsersCache = cacheData.allUsers;
            this.allUsersCacheTimestamp = cacheData.allUsersTimestamp;
          }
        }
        
        // Load live rides cache
        if (cacheData.liveRides) {
          for (const [key, value] of Object.entries(cacheData.liveRides)) {
            if (now - value.timestamp < this.config.LIVE_RIDES_TTL) {
              this.liveRidesCache.set(key, value);
            }
          }
        }
        
        console.log(`📦 Loaded dashboard cache: ${this.allSitesCache ? 'sites' : ''} ${this.allUsersCache ? 'users' : ''} ${this.liveRidesCache.size} live rides`);
      }
    } catch (error) {
      console.warn('⚠️ Failed to load dashboard cache from storage:', error);
    }
  }

  /**
   * Save cache to sessionStorage
   */
  saveToStorage() {
    try {
      const cacheData = {
        allSites: this.allSitesCache,
        allSitesTimestamp: this.allSitesCacheTimestamp,
        allUsers: this.allUsersCache,
        allUsersTimestamp: this.allUsersCacheTimestamp,
        liveRides: {},
      };
      
      // Convert live rides Map to object
      this.liveRidesCache.forEach((value, key) => {
        cacheData.liveRides[key] = value;
      });
      
      sessionStorage.setItem(this.config.STORAGE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('⚠️ Failed to save dashboard cache to storage:', error);
    }
  }

  /**
   * Check if a cache entry is valid
   */
  isValid(timestamp, ttl) {
    if (!timestamp) return false;
    const now = Date.now();
    return now - timestamp < ttl;
  }

  // ========== All Sites Cache ==========

  /**
   * Get cached all sites
   * @returns {Array|null} - Cached sites array or null
   */
  getAllSites() {
    if (!this.allSitesCache || !this.isValid(this.allSitesCacheTimestamp, this.config.ALL_SITES_TTL)) {
      this.allSitesCache = null;
      this.allSitesCacheTimestamp = null;
      return null;
    }
    
    console.log('✅ Cache hit for all sites');
    return this.allSitesCache;
  }

  /**
   * Set cached all sites
   * @param {Array} sites - Sites array
   */
  setAllSites(sites) {
    if (!sites || !Array.isArray(sites)) return;
    
    this.allSitesCache = sites;
    this.allSitesCacheTimestamp = Date.now();
    this.saveToStorage();
    console.log(`💾 Cached ${sites.length} sites`);
  }

  // ========== All Users Cache ==========

  /**
   * Get cached all users
   * @returns {Array|null} - Cached users array or null
   */
  getAllUsers() {
    if (!this.allUsersCache || !this.isValid(this.allUsersCacheTimestamp, this.config.ALL_USERS_TTL)) {
      this.allUsersCache = null;
      this.allUsersCacheTimestamp = null;
      return null;
    }
    
    console.log('✅ Cache hit for all users');
    return this.allUsersCache;
  }

  /**
   * Set cached all users
   * @param {Array} users - Users array
   */
  setAllUsers(users) {
    if (!users || !Array.isArray(users)) return;
    
    this.allUsersCache = users;
    this.allUsersCacheTimestamp = Date.now();
    this.saveToStorage();
    console.log(`💾 Cached ${users.length} users`);
  }

  // ========== Driver Stats Cache ==========

  /**
   * Get cached driver stats
   * @param {string} rideId - Ride ID
   * @param {string} userId - User ID
   * @returns {Object|null} - Cached stats or null
   */
  getDriverStats(rideId, userId) {
    if (!rideId || !userId) return null;
    
    const key = this.getDriverKey(rideId, userId);
    const entry = this.driverStatsCache.get(key);
    
    if (!entry || !this.isValid(entry.timestamp, this.config.DRIVER_STATS_TTL)) {
      if (entry) this.driverStatsCache.delete(key);
      return null;
    }
    
    return entry.data;
  }

  /**
   * Set cached driver stats
   * @param {string} rideId - Ride ID
   * @param {string} userId - User ID
   * @param {Object} stats - Driver stats
   */
  setDriverStats(rideId, userId, stats) {
    if (!rideId || !userId || !stats) return;
    
    const key = this.getDriverKey(rideId, userId);
    this.driverStatsCache.set(key, {
      data: stats,
      timestamp: Date.now(),
    });
  }

  // ========== Driver Status Cache ==========

  /**
   * Get cached driver status
   * @param {string} rideId - Ride ID
   * @param {string} userId - User ID
   * @returns {Object|null} - Cached status or null
   */
  getDriverStatus(rideId, userId) {
    if (!rideId || !userId) return null;
    
    const key = this.getDriverKey(rideId, userId);
    const entry = this.driverStatusCache.get(key);
    
    if (!entry || !this.isValid(entry.timestamp, this.config.DRIVER_STATUS_TTL)) {
      if (entry) this.driverStatusCache.delete(key);
      return null;
    }
    
    return entry.data;
  }

  /**
   * Set cached driver status
   * @param {string} rideId - Ride ID
   * @param {string} userId - User ID
   * @param {Object} status - Driver status
   */
  setDriverStatus(rideId, userId, status) {
    if (!rideId || !userId || !status) return;
    
    const key = this.getDriverKey(rideId, userId);
    this.driverStatusCache.set(key, {
      data: status,
      timestamp: Date.now(),
    });
  }

  // ========== Live Rides Cache ==========

  /**
   * Get cached live rides for a date
   * @param {string} date - Date in YYYY-MM-DD format
   * @returns {Array|null} - Cached rides array or null
   */
  getLiveRides(date) {
    if (!date) return null;
    
    const key = this.getLiveRidesKey(date);
    const entry = this.liveRidesCache.get(key);
    
    if (!entry || !this.isValid(entry.timestamp, this.config.LIVE_RIDES_TTL)) {
      if (entry) this.liveRidesCache.delete(key);
      return null;
    }
    
    console.log(`✅ Cache hit for live rides on ${date}`);
    return entry.data;
  }

  /**
   * Set cached live rides for a date
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {Array} rides - Rides array
   */
  setLiveRides(date, rides) {
    if (!date || !rides || !Array.isArray(rides)) return;
    
    const key = this.getLiveRidesKey(date);
    this.liveRidesCache.set(key, {
      data: rides,
      timestamp: Date.now(),
    });
    this.saveToStorage();
    console.log(`💾 Cached ${rides.length} live rides for ${date}`);
  }

  /**
   * Clear all cached data
   */
  clearAll() {
    this.allSitesCache = null;
    this.allSitesCacheTimestamp = null;
    this.allUsersCache = null;
    this.allUsersCacheTimestamp = null;
    this.driverStatsCache.clear();
    this.driverStatusCache.clear();
    this.liveRidesCache.clear();
    
    try {
      sessionStorage.removeItem(this.config.STORAGE_KEY);
    } catch (error) {
      console.warn('⚠️ Failed to clear cache from storage:', error);
    }
    console.log('🗑️ Cleared all dashboard cache');
  }

  /**
   * Clear live rides cache (useful for refresh)
   */
  clearLiveRides() {
    this.liveRidesCache.clear();
    this.saveToStorage();
    console.log('🗑️ Cleared live rides cache');
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      hasAllSites: !!this.allSitesCache && this.isValid(this.allSitesCacheTimestamp, this.config.ALL_SITES_TTL),
      hasAllUsers: !!this.allUsersCache && this.isValid(this.allUsersCacheTimestamp, this.config.ALL_USERS_TTL),
      driverStatsSize: this.driverStatsCache.size,
      driverStatusSize: this.driverStatusCache.size,
      liveRidesSize: this.liveRidesCache.size,
    };
  }
}

// Export singleton instance
export default new DashboardCache();

